<script setup>
/**
 * This component is responsible for handling the logic related to session
 * timeouts and the associated access token lifecycle in a web view context,
 * prompting the user with warnings as the session nears expiry.
 */
import { computed, ref, watch } from 'vue';
import dayjs from 'dayjs';
import { useIntervalFn, watchOnce } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { useWebViewContext } from '../../composables/webview-context';
import BaseModal from '../headless/BaseModal.vue';
import AppButton from '../atoms/app/AppButton.vue';
import TextTimer from '../atoms/TextTimer.vue';
import { track, trackVisibility } from '../../utils/tracking';
import { PAGE_SENDING } from '../../routes';
import { wait } from '../../utils/wait';

const {
    authExpirationTimestamp,
    deeplinkToAppRoute,
} = useWebViewContext();

const showWarningModal = ref(false);

const now = ref(dayjs());

// Update the now value every second to make it reactive
useIntervalFn(() => {
    now.value = dayjs();
}, 1000, { immediate: true });

const minutesLeft = computed(() => {
    const expiry = dayjs.unix(authExpirationTimestamp.value);
    return expiry.diff(now.value, 'minutes');
});

const secondsLeft = computed(() => {
    const expiry = dayjs.unix(authExpirationTimestamp.value);
    return expiry.diff(now.value, 'seconds');
});

/* First warning modal */
const MINUTES_LEFT_WHEN_FIRST_WARNING_SHOULD_BE_SHOWN = 20;
const firstWarningTriggered = computed(() => minutesLeft.value < MINUTES_LEFT_WHEN_FIRST_WARNING_SHOULD_BE_SHOWN);
watchOnce(firstWarningTriggered, () => {
    showWarningModal.value = true;
    trackVisibility({
        element: `APP_AP: ${MINUTES_LEFT_WHEN_FIRST_WARNING_SHOULD_BE_SHOWN} Minute Warning Modal`,
    });
});

/* Second warning modal */
const MINUTES_LEFT_WHEN_SECOND_WARNING_SHOULD_BE_SHOWN = 5;
const secondWarningTriggered = computed(() => minutesLeft.value < MINUTES_LEFT_WHEN_SECOND_WARNING_SHOULD_BE_SHOWN);
watchOnce(secondWarningTriggered, () => {
    showWarningModal.value = true;
    trackVisibility({
        element: `APP_AP: ${MINUTES_LEFT_WHEN_SECOND_WARNING_SHOULD_BE_SHOWN} Minute Warning Modal`,
    });
});

/* Track closing of the modals */
watch(showWarningModal, (isShown) => {
    if (isShown) return;

    if (secondWarningTriggered.value) {
        track({
            element: `APP_AP: ${MINUTES_LEFT_WHEN_SECOND_WARNING_SHOULD_BE_SHOWN} Minute Warning Modal Closed`,
        });
    } else if (firstWarningTriggered.value) {
        track({
            element: `APP_AP: ${MINUTES_LEFT_WHEN_FIRST_WARNING_SHOULD_BE_SHOWN} Minute Warning Modal Closed`,
        });
    }
});

/* Redirect user when time runs out */
const timeRanOut = computed(() => secondsLeft.value <= 0);
const route = useRoute();
watchOnce(timeRanOut, async () => {
    // Don't redirect if form is sending already
    if (route.matched.some(({ name }) => name === PAGE_SENDING)) return;

    track({
        element: 'APP_AP: Webview timed out',
    });

    // Wait a bit for the track to go through
    await wait(1000);

    deeplinkToAppRoute('application-aborted');
});

</script>

<template>
    <div>
        <BaseModal v-model:open="showWarningModal">
            <template #headline>
                Verbleibende Zeit: <TextTimer :expire-timestamp="authExpirationTimestamp"/>
            </template>
            <template #default>
                <div class="">
                    <p>
                        <template v-if="!secondWarningTriggered">
                            Fülle das Formular innerhalb der verbleibenden Zeit aus und schicke deine Bewerbung ab.
                        </template>
                        Nach Ablauf der Zeit wird das Formular geschlossen und deine Änderungen gehen verloren.
                    </p>
                </div>
            </template>
            <template #actions>
                <div class="inline-flex flex-col gap-s">
                    <AppButton @click="showWarningModal = false">
                        Verstanden
                    </AppButton>
                </div>
            </template>
        </BaseModal>
    </div>
</template>

<style lang="scss">

</style>
